import { useId } from 'react';

import { cn } from '@/lib/cn';

export interface SwitchProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Hide the visible label text (still announced to assistive tech via aria-label). */
  hideLabel?: boolean;
}

export default function Switch({ label, checked, onChange, disabled, id, className, hideLabel }: SwitchProps) {
  const autoId = useId();
  const buttonId = id ?? autoId;
  return (
    <div className={cn('inline-flex items-center gap-3', className)}>
      <button
        id={buttonId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={hideLabel ? label : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
          'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:ring-offset-1 focus-visible:ring-offset-(--bg-surface-strong) focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'border-(--accent-primary-strong) bg-(--accent-primary-strong)' : 'border-(--border-default) bg-(--bg-surface)',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
      {!hideLabel && (
        <label htmlFor={buttonId} className="text-sm text-(--text-primary)">
          {label}
        </label>
      )}
    </div>
  );
}
